import { useState, useEffect, useCallback, useMemo } from 'react';
import { engagementService } from 'api/services/engagementService';
import type {
  ID, PaginatedResponse, Engagement,
  CreateEngagementRequest, UpdateEngagementRequest, EngagementSearchRequest,
  ClosurePreview, CloseEngagementRequest, ReopenEngagementRequest, EngagementClosure,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
  hasNextPage: false, hasPreviousPage: false,
};

export const useEngagements = (params?: EngagementSearchRequest) => {
  const [data, setData] = useState<Engagement[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Engagement>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchEngagements = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await engagementService.getAll(params);
      setData(r.data); setPagination(r.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchEngagements(); }, [fetchEngagements]);
  return { data, pagination, loading, error, refetch: fetchEngagements };
};

export const useEngagement = (id: ID | null | undefined) => {
  const [data, setData] = useState<Engagement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetch = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true); setError(null);
    try { const r = await engagementService.getById(id); setData(r.data); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

export const useCreateEngagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const createEngagement = async (data: CreateEngagementRequest, options?: { onSuccess?: (item: Engagement) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await engagementService.create(data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { createEngagement, loading, error };
};

export const useUpdateEngagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const updateEngagement = async (id: ID, data: UpdateEngagementRequest, options?: { onSuccess?: (item: Engagement) => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { const r = await engagementService.update(id, data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { updateEngagement, loading, error };
};

export const useDeleteEngagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const deleteEngagement = async (id: ID, options?: { onSuccess?: () => void; onError?: (e: Error) => void }) => {
    setLoading(true); setError(null);
    try { await engagementService.delete(id); options?.onSuccess?.(); }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { deleteEngagement, loading, error };
};

// ----------------------------------------------------------------------------
// Closure / Reopen
// ----------------------------------------------------------------------------

/**
 * Closure pre-flight payload for the close-engagement modal.
 * Does NOT auto-fetch — callers pass `enabled` (e.g. modal open state) to gate.
 */
export const useClosurePreview = (id: ID | null | undefined, enabled: boolean = true) => {
  const [data, setData] = useState<ClosurePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id || !enabled) { setData(null); return; }
    setLoading(true); setError(null);
    try { setData(await engagementService.getClosurePreview(id)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id, enabled]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

export const useCloseEngagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const closeEngagement = async (
    id: ID,
    data: CloseEngagementRequest,
    options?: { onSuccess?: (item: Engagement) => void; onError?: (e: Error) => void },
  ) => {
    setLoading(true); setError(null);
    try { const r = await engagementService.close(id, data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { closeEngagement, loading, error };
};

export const useReopenEngagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const reopenEngagement = async (
    id: ID,
    data: ReopenEngagementRequest,
    options?: { onSuccess?: (item: Engagement) => void; onError?: (e: Error) => void },
  ) => {
    setLoading(true); setError(null);
    try { const r = await engagementService.reopen(id, data); options?.onSuccess?.(r.data); return r.data; }
    catch (err) { const e = err as Error; setError(e); options?.onError?.(e); throw e; }
    finally { setLoading(false); }
  };
  return { reopenEngagement, loading, error };
};

export const useClosureHistory = (id: ID | null | undefined, enabled: boolean = true) => {
  const [data, setData] = useState<EngagementClosure[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id || !enabled) { setData([]); return; }
    setLoading(true); setError(null);
    try { setData(await engagementService.getClosureHistory(id)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [id, enabled]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};
